import { axiosInstance } from "@src/api/axiosConfig";
import { useMutation, useQuery } from "react-query";

const nestPath = process.env.REACT_APP_NEST_API_URL;
const adPath = `${nestPath}/expert/region-ad`;

// 지역광고 가능 여부 조회
interface checkRegionalADAvailabilityResponse {
    availableAd: boolean;
    availableTicketCount: number;
    freeEventStartedAt: string;
    freeEventEndedAt: string;
}

export const checkRegionalADAvailability = async (pnu: string): Promise<checkRegionalADAvailabilityResponse> => {
    const { data } = await axiosInstance.get<checkRegionalADAvailabilityResponse>(`${adPath}/check`, {
        params: { pnu },
    });
    return data;
};

export const useGetCheckRegioADAvailability = (pnu: string, enabled?: boolean) => {
    return useQuery<checkRegionalADAvailabilityResponse>({
        queryKey: [pnu],
        queryFn: () => checkRegionalADAvailability(pnu),
        enabled: enabled,
    });
};

// 광고 가능 매물 갯수 조회
interface getAvailableAdCountResponse {
    totalCount: number;
    appliedItemCount: number;
    isAvailableRegionAd: boolean;
}

export const getAvailableAdCount = async (pnu: string): Promise<getAvailableAdCountResponse> => {
    const { data } = await axiosInstance.get<getAvailableAdCountResponse>(`${adPath}/available/count`, {
        params: { pnu },
    });
    return data;
};

export const useGetAvailableAdCount = (pnu: string, enabled?: boolean) => {
    return useQuery<getAvailableAdCountResponse>({
        queryKey: [pnu, "availableAdCount"],
        queryFn: () => getAvailableAdCount(pnu),
        enabled: enabled,
    });
};

// 티켓 카운트 조회
export interface getTicketCountResponse {
    deungiTicketCount: string;
    existDeungiTicketCount: string;
    itemAdsTicket: string;
    regionAdsTicket: string;
}

export const getTicketCount = async (): Promise<getTicketCountResponse> => {
    const { data } = await axiosInstance.get<getTicketCountResponse>(`${nestPath}/ticket/count/total`);
    return data;
};

export const useGetTicketCount = (enabled?: boolean) => {
    return useQuery({
        queryKey: ["ticketCount"],
        queryFn: () => getTicketCount(),
        enabled: enabled,
    });
};

// 내가 광고중인 매물
export interface getMyADCountRequest {
    caseId?: string;
    adStatus?: "normal" | "closed";
}

export interface getMyADCountResponse {
    list: {
        bannerNo: number;
        bidType: string;
        itemId: string;
        itemSubId: number;
        image: string;
        address: string;
        use: string;
        court: string;
        bidDate: string;
        minPrice: string;
        discountRate: string;
        startedAt: string;
        expiredAt: string;
        bannerStatus: number;
    }[];
    countInfo: {
        totalCount: number;
        normalCount: number;
        closedCount: number;
    };
}

export const getMyADCount = async (params: getMyADCountRequest): Promise<getMyADCountResponse> => {
    const { data } = await axiosInstance.get<getMyADCountResponse>(`${nestPath}/expert-banner/item-list`, {
        params,
    });
    return data;
};

export const useGetMyADCount = (params: getMyADCountRequest, enabled?: boolean) => {
    return useQuery({
        queryKey: ["myADCount", params],
        queryFn: () => getMyADCount(params),
        enabled: enabled,
    });
};

// 내가 광고 중인 지역
export interface getMyRegionADCountRequest {
    adStatus?: "normal" | "closed";
}

export interface getMyRegionADCountResponse {
    list: {
        regionAdNo: number;
        address: string;
        startedAt: string;
        expiredAt: string;
    }[];
    countInfo: {
        totalCount: number;
        normalCount: number;
        closedCount: number;
    };
}

export const getMyRegionADCount = async (params: getMyRegionADCountRequest): Promise<getMyRegionADCountResponse> => {
    const { data } = await axiosInstance.get<getMyRegionADCountResponse>(`${adPath}/my`, {
        params,
    });
    return data;
};

export const useGetMyRegionADCount = (params: getMyRegionADCountRequest, enabled?: boolean) => {
    return useQuery({
        queryKey: ["myRegionADCount", params],
        queryFn: () => getMyRegionADCount(params),
        enabled: enabled,
    });
};

// 지역 광고 취소
export const cancelRegionAD = async (regionAdNo: number) => {
    const { data } = await axiosInstance.delete(`${adPath}/${regionAdNo}`);
    return data;
};

export const useCancelRegionAD = () => {
    return useMutation({
        mutationFn: (regionAdNo: number) => cancelRegionAD(regionAdNo),
    });
};

// 매물 광고 취소
export const cancelItemAD = async (bannerNo: number) => {
    const { data } = await axiosInstance.delete(`${nestPath}/expert-banner/banner`, {
        params: { bannerNo },
    });
    return data;
};

export const useCancelItemAD = () => {
    return useMutation({
        mutationFn: (bannerNo: number) => cancelItemAD(bannerNo),
    });
};

// 건축사 리스트 조회
export interface getArchitectListRequest {
    pnu: string;
}

export interface getArchitectListResponse {
    list: {
        ceo: string;
        image: string;
        no: number;
        officeAddress: string;
        officeName: string;
        officePhone: string;
        userNo: number;
    }[];
}

export const getArchitectList = async (params: getArchitectListRequest): Promise<getArchitectListResponse> => {
    const { data } = await axiosInstance.get(`${nestPath}/expert-banner/architects`, { params });
    return data;
};

export const useGetArchitectList = (params: getArchitectListRequest, enabled?: boolean) => {
    return useQuery<getArchitectListResponse>({
        queryKey: ["architectList", params],
        queryFn: () => getArchitectList(params),
        enabled: enabled,
    });
};

// 타입별 소모성 상품 개수 조회
export interface getTicketCountByTypeResponse {
    curHaveCnt: number;
    dayLeft7Cnt: number;
}

export const getTicketCountByType = async (ticketType: number): Promise<getTicketCountByTypeResponse> => {
    const { data } = await axiosInstance.get(`${nestPath}/ticket/count/${ticketType}`);
    return data;
};

export const useGetTicketCountByType = (ticketType: number, enabled?: boolean) => {
    return useQuery<getTicketCountByTypeResponse>({
        queryKey: ["ticketCountByType", ticketType],
        queryFn: () => getTicketCountByType(ticketType),
        enabled: enabled,
    });
};
